import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, CalendarDays, Clock, Check, Medal, Shirt, Ticket, Camera, HeartPulse, CupSoda, Ruler, Sparkles,
  ExternalLink, Handshake, Trophy, Timer, Gift, Coffee, Droplets, Bus, Utensils, Music, Award, Star, Heart, Flag, Zap,
  ShieldCheck, Backpack, Stethoscope, Car, Mountain, Footprints } from "lucide-react";
import { http, mediaUrl, mediaSrcSet } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/site/Seo";
import { CategoryCard } from "@/components/site/CategoryCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ICONS = { Check, Medal, Shirt, Ticket, Camera, HeartPulse, CupSoda, Ruler, Sparkles, Trophy, Timer, Gift, Coffee,
  Droplets, Bus, Utensils, Music, Award, Star, Heart, Flag, Zap, ShieldCheck, Backpack, Stethoscope, Car, Mountain, Footprints, MapPin, Clock };

function InclusionIcon({ name, ...props }) {
  const known = ICONS[name];
  const [Loaded, setLoaded] = React.useState(null);
  React.useEffect(() => {
    if (known || !name) return;
    import("lucide-react").then((m) => { if (m[name]) setLoaded(() => m[name]); });
  }, [known, name]);
  const Icon = known || Loaded || Check;
  return <Icon {...props} />;
}

const EditionGallery = React.lazy(() => import("@/components/site/EditionGallery"));

const lazyImg = { loading: "lazy", decoding: "async" };

const useData = (key, url) => useQuery({ queryKey: [key], queryFn: async () => (await http.get(url)).data, staleTime: 0, refetchOnMount: "always" });

function useCountdown(target) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (isNaN(diff)) return null;
  if (diff <= 0) return { done: true };
  const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000), s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: false };
}

function HeroCountdown({ content }) {
  const cd = content?.countdown || {};
  const dl = content?.deadline || {};
  const t = useCountdown(cd.target_date);
  const daysTo = (dt) => { if (!dt) return null; const n = Math.ceil((new Date(dt).getTime() - Date.now()) / 86400000); return n; };
  const regDays = daysTo(dl.reg_close), ebDays = daysTo(dl.earlybird_close);
  const type = dl.type || "none";
  const cells = t && !t.done ? [["Days", t.d], ["Hrs", t.h], ["Min", t.m], ["Sec", t.s]] : null;
  return (
    <div className="mt-10">
      {cd.enabled && cells && (
        <div data-testid="hero-countdown">
          {cd.heading && <p className="text-xs uppercase tracking-[0.25em] font-bold text-white/70 mb-3">{cd.heading}</p>}
          <div className="flex gap-3">
            {cells.map(([lbl, val]) => (
              <div key={lbl} className="bg-white/10 backdrop-blur border border-white/15 px-4 py-3 text-center min-w-[68px]">
                <div className="font-display text-4xl leading-none text-[#FF4D00]">{String(val).padStart(2, "0")}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{lbl}</div>
              </div>
            ))}
          </div>
          {cd.subtext && <p className="text-sm text-white/50 mt-3">{cd.subtext}</p>}
        </div>
      )}
      {dl.enabled && type !== "none" && (
        <div className="flex flex-wrap gap-3 mt-5" data-testid="hero-deadline">
          {(type === "earlybird" || type === "both") && ebDays != null && (
            <span className="inline-block bg-[#F59E0B] text-[#0b1120] text-xs uppercase tracking-widest font-bold px-4 py-2">
              {ebDays > 0 ? `Early bird ends in ${ebDays} day${ebDays > 1 ? "s" : ""}` : "Early bird ended"}
            </span>
          )}
          {(type === "registration" || type === "both") && regDays != null && (
            <span className="inline-block bg-[#FF4D00] text-white text-xs uppercase tracking-widest font-bold px-4 py-2">
              {regDays > 0 ? `Registrations close in ${regDays} day${regDays > 1 ? "s" : ""}` : "Registrations closed"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const Section = ({ id, kicker, title, children, className = "" }) => (
  <section id={id} className={`relative max-w-7xl mx-auto px-5 sm:px-8 py-24 ${className}`}>
    {kicker && <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF4D00] mb-4">{kicker}</p>}
    {title && <h2 className="font-display text-5xl sm:text-6xl uppercase tracking-tight mb-14 max-w-3xl leading-[0.95]">{title}</h2>}
    {children}
  </section>
);

export default function HomePage() {
  const navigate = useNavigate();
  const { data: event } = useData("event", "/event");
  const { data: content } = useData("content", "/content");
  const { data: categories } = useData("categories", "/categories");

  const dateStr = event?.date ? new Date(event.date).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="bg-[#0b1120] min-h-screen overflow-x-hidden">
      <Seo content={content} event={event} />
      <Header />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center grain" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          {content?.hero_image && (
            <img src={mediaUrl(content.hero_image, 1280)} srcSet={mediaSrcSet(content.hero_image, [640, 1080, 1600, 1920])}
              sizes="100vw" fetchPriority="high" decoding="async" alt="runners" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b1120] via-[#0b1120]/70 to-[#0b1120]/30" />
          <div className="absolute inset-0 bg-[#0b1120]/40" />
        </div>
        {content && (
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-24">
          <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
            className="text-xs sm:text-sm uppercase tracking-[0.35em] font-bold text-[#FF4D00] mb-6">
            {content?.hero_kicker || "SUNDAY · OCTOBER 11 · 2026"}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-6xl sm:text-8xl md:text-9xl uppercase leading-[0.85] tracking-tight whitespace-pre-line max-w-4xl"
            data-testid="hero-title">
            {content?.hero_title || "RUN THE CITY."}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg text-white/70 max-w-xl mt-8 leading-relaxed" data-testid="hero-tagline">
            {content?.hero_tagline}
          </motion.p>
          <HeroCountdown content={content} />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }}
            className="flex flex-wrap items-center gap-4 mt-10">
            <button onClick={() => navigate("/register")}
              className="inline-flex items-center gap-3 bg-[#FF4D00] hover:bg-[#FF6A22] text-white text-sm uppercase tracking-[0.18em] font-bold px-9 py-5 transition-colors active:scale-95"
              data-testid="hero-cta-button">
              Claim your bib <ArrowRight className="h-4 w-4" />
            </button>
            <a href="#categories" className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white text-sm uppercase tracking-[0.18em] font-bold px-9 py-5 transition-colors">
              View races
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="flex flex-wrap gap-8 mt-16 text-sm text-white/60">
            <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#FF4D00]" /> {event?.day}, {dateStr}</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#FF4D00]" /> {event?.venue}, {event?.city}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#FF4D00]" /> Flag-off {event?.start_time}</span>
          </motion.div>
        </div>
        )}
      </section>

      {/* CATEGORIES */}
      <Section id="categories" kicker="Pick your distance" title="Five races. One finish line.">
        {content?.promo_banner?.enabled !== false && (content?.promo_banner?.line1 || content?.promo_banner?.line2) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 240, damping: 18 }}
            className="mb-8 flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-1 text-center bg-gradient-to-r from-[#EF4444] via-[#FF4D00] to-[#F59E0B] px-6 py-4"
            data-testid="promo-banner"
          >
            <span className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-white leading-none">{content?.promo_banner?.line1 || "🔥 FLAT 30% OFF"}</span>
            <span className="text-white/90 text-xs uppercase tracking-[0.2em] font-bold">{content?.promo_banner?.line2 || "Limited-time offer"}</span>
          </motion.div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories?.map((c, i) => <CategoryCard key={c.id} cat={c} index={i} />)}
        </div>
      </Section>

      {/* ABOUT */}
      <section id="about" className="relative border-y border-white/10 bg-[#0d1424]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF4D00] mb-4">The event</p>
            <h2 className="font-display text-5xl sm:text-6xl uppercase tracking-tight mb-8 leading-[0.95]">{content?.about_title || "More than a race"}</h2>
            <p className="text-white/60 leading-relaxed text-lg">{content?.about_body}</p>
          </div>
          <div className="relative aspect-[4/3] border border-white/10">
            {(content?.previous_edition?.images?.[0] || content?.hero_image) && (
              <img src={mediaUrl(content?.previous_edition?.images?.[0] || content?.hero_image, 1080)} {...lazyImg} alt="about" className="w-full h-full object-cover" />
            )}
            <div className="absolute bottom-0 left-0 bg-[#FF4D00] px-6 py-4">
              <span className="font-display text-4xl">42.1KM</span>
              <span className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#0b1120]">to glory</span>
            </div>
          </div>
        </div>
      </section>

      {/* ROUTE MAPS */}
      {content?.route_maps?.enabled !== false && (
        <Section id="course" kicker={content?.route_maps?.kicker || "The route"} title={content?.route_maps?.title || "A course built for personal bests."}>
          {(content?.route_maps?.maps || []).length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6" data-testid="route-maps">
              {content.route_maps.maps.map((m, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }} className="border border-white/10 bg-[#111827] overflow-hidden" data-testid={`route-map-${i}`}>
                  {m.image_url && <img src={mediaUrl(m.image_url, 1080)} {...lazyImg} alt={m.label} className="w-full aspect-[16/10] object-cover" />}
                  <div className="p-6">
                    <h3 className="font-display text-2xl uppercase tracking-wide">{m.label}</h3>
                    {m.description && <p className="text-white/50 text-sm mt-2 leading-relaxed">{m.description}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="relative aspect-[21/9] border border-white/10 bg-[#111827] flex items-center justify-center grain overflow-hidden">
              <div className="text-center relative z-10">
                <MapPin className="h-10 w-10 text-[#FF4D00] mx-auto mb-4" />
                <p className="font-display text-3xl uppercase tracking-wide">Course map coming soon</p>
                <p className="text-white/40 text-sm mt-2">Rolling, scenic trails through Coorg's misty coffee hills.</p>
              </div>
            </div>
          )}
        </Section>
      )}

      {/* INCLUSIONS */}
      <section id="inclusions" className="border-y border-white/10 bg-[#0d1424]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF4D00] mb-4">What's in the pack</p>
          <h2 className="font-display text-5xl sm:text-6xl uppercase tracking-tight mb-14 leading-[0.95]">Every runner gets more.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {content?.inclusions?.map((inc, i) => {
              return (
                <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }} className="bg-[#0d1424] p-10 hover:bg-[#111827] transition-colors" data-testid={`inclusion-${i}`}>
                  <InclusionIcon name={inc.icon} className="h-8 w-8 text-[#FF4D00] mb-6" strokeWidth={1.75} />
                  <h3 className="font-display text-2xl uppercase tracking-wide mb-2">{inc.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{inc.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TSHIRT */}
      {content?.tshirt?.enabled !== false && (content?.tshirt?.image_url || content?.tshirt?.description) && (
        <Section id="tshirt" kicker={content?.tshirt?.kicker || "Wear the finish"} title={content?.tshirt?.title || "The 2027 Event Tee"}>
          <div className="grid lg:grid-cols-2 gap-14 items-center" data-testid="tshirt-section">
            <div className="relative aspect-square border border-white/10 bg-[#111827] overflow-hidden">
              {content?.tshirt?.image_url
                ? <img src={mediaUrl(content.tshirt.image_url, 1080)} {...lazyImg} alt="event tee" className="w-full h-full object-cover" />
                : <div className="absolute inset-0 flex items-center justify-center"><Shirt className="h-20 w-20 text-white/20" /></div>}
            </div>
            <div>
              <p className="text-white/60 text-lg leading-relaxed">{content?.tshirt?.description}</p>
              <div className="mt-8 space-y-4">
                {content?.tshirt?.sizes && (
                  <div className="flex items-center gap-3"><Ruler className="h-5 w-5 text-[#FF4D00]" /><span className="text-white/70 text-sm uppercase tracking-wider">{content.tshirt.sizes}</span></div>
                )}
                {content?.tshirt?.fabric && (
                  <div className="flex items-center gap-3"><Sparkles className="h-5 w-5 text-[#FF4D00]" /><span className="text-white/70 text-sm">{content.tshirt.fabric}</span></div>
                )}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* RACE-DAY INSTRUCTIONS */}
      {content?.race_day?.enabled !== false && (content?.race_day?.items || []).length > 0 && (
        <section id="race-day" className="border-y border-white/10 bg-[#0d1424]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF4D00] mb-4">{content?.race_day?.kicker || "Be ready"}</p>
            <h2 className="font-display text-5xl sm:text-6xl uppercase tracking-tight mb-14 leading-[0.95]">{content?.race_day?.title || "Race-day instructions"}</h2>
            <div className="space-y-px bg-white/10 border border-white/10" data-testid="race-day-section">
              {content.race_day.items.map((it, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }} className="bg-[#0d1424] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 hover:bg-[#111827] transition-colors" data-testid={`race-day-item-${i}`}>
                  <span className="font-display text-2xl text-[#FF4D00] uppercase w-32 shrink-0">{it.time}</span>
                  <div>
                    <h3 className="font-display text-xl uppercase tracking-wide">{it.title}</h3>
                    {it.desc && <p className="text-white/50 text-sm mt-1 leading-relaxed">{it.desc}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PREVIOUS EDITION */}
      <Section id="gallery" kicker="Look back" title={content?.previous_edition?.title || "Previous Edition"}>        {content?.previous_edition?.description && (
          <p className="text-white/60 text-lg max-w-2xl -mt-8 mb-12 leading-relaxed">{content.previous_edition.description}</p>
        )}
        <React.Suspense fallback={<div className="aspect-[4/3] sm:aspect-[21/9] border border-white/10 bg-[#111827]" />}>
          <EditionGallery images={content?.previous_edition?.images || []} />
        </React.Suspense>
      </Section>

      {/* HOTELS / STAY */}
      {content?.hotels?.enabled !== false && (content?.hotels?.items || []).length > 0 && (
        <Section id="stay" kicker={content?.hotels?.kicker || "Where to stay"} title={content?.hotels?.title || "Partner stays in Coorg"}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hotels-section">
            {content.hotels.items.map((h, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.06 }} className="border border-white/10 bg-[#111827] overflow-hidden flex flex-col" data-testid={`hotel-${i}`}>
                {h.image_url && <img src={mediaUrl(h.image_url, 828)} {...lazyImg} alt={h.name} className="w-full aspect-[4/3] object-cover" />}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-2xl uppercase tracking-wide">{h.name}</h3>
                  {h.distance && <p className="text-[#FF4D00] text-xs uppercase tracking-widest mt-1">{h.distance}</p>}
                  {h.description && <p className="text-white/50 text-sm mt-3 leading-relaxed flex-1">{h.description}</p>}
                  <div className="flex items-center justify-between mt-5">
                    {h.price && <span className="text-white/70 text-sm font-bold">{h.price}</span>}
                    {h.url && <a href={h.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#FF4D00] text-xs uppercase tracking-widest font-bold hover:gap-2 transition-all" data-testid={`hotel-link-${i}`}>Book <ExternalLink className="h-3 w-3" /></a>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      {/* PARTNER CARDS */}
      {content?.partner_cards?.enabled !== false && (content?.partner_cards?.items || []).length > 0 && (
        <section id="partners" className="border-y border-white/10 bg-[#0d1424]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF4D00] mb-4">{content?.partner_cards?.kicker || "Plan your trip"}</p>
            <h2 className="font-display text-5xl sm:text-6xl uppercase tracking-tight mb-14 leading-[0.95]">{content?.partner_cards?.title || "Travel & experience partners"}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partner-cards-section">
              {content.partner_cards.items.map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }} className="border border-white/10 bg-[#111827] p-7 flex flex-col" data-testid={`partner-card-${i}`}>
                  {p.logo_url
                    ? <img src={mediaUrl(p.logo_url, 480)} {...lazyImg} alt={p.name} className="h-14 object-contain object-left mb-5" />
                    : <Handshake className="h-10 w-10 text-[#FF4D00] mb-5" />}
                  {p.tag && <p className="text-[#FF4D00] text-[11px] uppercase tracking-widest mb-1">{p.tag}</p>}
                  <h3 className="font-display text-2xl uppercase tracking-wide">{p.name}</h3>
                  {p.description && <p className="text-white/50 text-sm mt-3 leading-relaxed flex-1">{p.description}</p>}
                  {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#FF4D00] text-xs uppercase tracking-widest font-bold mt-5 hover:gap-2 transition-all" data-testid={`partner-link-${i}`}>Learn more <ExternalLink className="h-3 w-3" /></a>}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPONSORS */}
      <section className="border-y border-white/10 py-20 bg-[#080d18]" data-testid="sponsors-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-center text-xs uppercase tracking-[0.25em] font-bold text-[#FF4D00] mb-12">Our Partners & Sponsors</p>
          {(() => {
            const list = (content?.sponsors_v2 || []).filter((s) => s.enabled !== false);
            const order = ["Title Sponsor", "Co-Powered By", "Powered By", "Associate Sponsor", "Official Partner", "Community Partner", "Supporting Partner"];
            const groups = {};
            list.forEach((s) => { (groups[s.title || "Partner"] = groups[s.title || "Partner"] || []).push(s); });
            const titles = Object.keys(groups).sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99));
            return (
              <div className="space-y-12">
                {titles.map((title) => (
                  <div key={title} data-testid={`sponsor-group-${title}`}>
                    <p className="text-center text-[11px] uppercase tracking-[0.3em] text-white/60 mb-6">{title}</p>
                    <div className="flex flex-wrap justify-center items-center gap-x-14 gap-y-8">
                      {groups[title].map((s, i) => (
                        <div key={i} className="flex items-center justify-center h-16">
                          {s.logo_url ? (
                            <img src={mediaUrl(s.logo_url, 480)} {...lazyImg} alt={s.name} className="max-h-16 max-w-[180px] object-contain hover:scale-105 transition-transform" />
                          ) : (
                            <span className="font-display text-3xl uppercase tracking-wide text-white/60 hover:text-white transition-colors">{s.name}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* FAQ */}
      <Section id="faqs" kicker="Good to know" title="Questions, answered.">
        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {content?.faqs?.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-display text-2xl uppercase tracking-wide hover:text-[#FF4D00] hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/60 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* CTA strip */}
      <section className="bg-[#FF4D00] py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-display text-5xl sm:text-7xl uppercase tracking-tight leading-[0.9] max-w-2xl">Your start line is waiting.</h2>
          <button onClick={() => navigate("/register")}
            className="inline-flex items-center gap-3 bg-[#0b1120] hover:bg-black text-white text-sm uppercase tracking-[0.18em] font-bold px-10 py-6 transition-colors active:scale-95 shrink-0"
            data-testid="cta-strip-register">
            Register now <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <Footer content={content} />
    </div>
  );
}
